import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
	Section,
	SectionCreatePayload,
	SectionStudentEnrollment,
	SectionUpdatePayload,
	User,
} from '@mira-avant/mtlibs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class SectionService {
	private readonly BASE_PATH = 'sections';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(private api: ApiService) {}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async getSectionsByClient$(
		clientUuid: string,
		page: number,
		pageSize: number,
		includeUsers: boolean = false,
		includeProducts: boolean = false
	): Promise<Section[]> {
		return this.withLoading(async () => {
			const params = { includeUsers: includeUsers.toString(), includeProducts: includeProducts.toString() };
			const sections = await firstValueFrom(
				this.api.get<Section[]>(
					`${this.BASE_PATH}/client/${clientUuid}/${page * pageSize}/${pageSize}`,
					{ params }
				)
			);
			return sections;
		});
	}

	public async getSectionsByCourse$(
		courseUuid: string,
		page: number,
		pageSize: number,
		includeUsers: boolean = false,
		includeProducts: boolean = false
	): Promise<Section[]> {
		return this.withLoading(async () => {
			const params = { includeUsers: includeUsers.toString(), includeProducts: includeProducts.toString() };
			const sections = await firstValueFrom(
				this.api.get<Section[]>(
					`${this.BASE_PATH}/course/${courseUuid}/${page * pageSize}/${pageSize}`,
					{ params }
				)
			);
			return sections;
		});
	}

	public async getMySections$(page: number, pageSize: number, includeProducts: boolean = false): Promise<Section[]> {
		return this.withLoading(async () => {
			const params = { includeProducts: includeProducts.toString() };
			const sections = await firstValueFrom(
				this.api.get<Section[]>(
					`${this.BASE_PATH}/my-sections/${page * pageSize}/${pageSize}`,
					{ params }
				)
			);
			return sections;
		});
	}

	public async hasSections(courseUuid: string): Promise<boolean> {
		return this.withLoading(async () => {
			const hasSections = await firstValueFrom(
				this.api.get<boolean>(`${this.BASE_PATH}/course/${courseUuid}/has-sections`)
			);

			return hasSections;
		});
	}

	public async searchSections(courseUuid: string, searchTerm: string): Promise<Section[]> {
		if (!searchTerm) {
			console.warn('Search term is required');
			return [];
		}

		return this.withLoading(async () => {
			const results = await firstValueFrom(
				this.api.get<Section[]>(`${this.BASE_PATH}/search/${courseUuid}/${searchTerm}`)
			);

			return results;
		});
	}

	public async getEnrollment(sectionUuid: string): Promise<SectionStudentEnrollment[]> {
		return this.withLoading(async () => {
			return await firstValueFrom<SectionStudentEnrollment[]>(
				this.api.get<SectionStudentEnrollment[]>(
					`${this.BASE_PATH}/section-enrollment/${sectionUuid}`
				)
			);
		});
	}

	public async updateEnrollment(
		sectionUuid: string,
		enrollment: SectionStudentEnrollment[]
	): Promise<boolean> {
		return this.withLoading(async () => {
			return await firstValueFrom<boolean>(
				this.api.put<boolean>(
					`${this.BASE_PATH}/section-enrollment/${sectionUuid}`,
					enrollment
				)
			);
		});
	}

	public async getTotalSectionsByClient(clientUuid: string): Promise<number> {
		return this.withLoading(async () => {
			return await firstValueFrom<number>(
				this.api.get<number>(`${this.BASE_PATH}/client/${clientUuid}/total-sections`)
			);
		});
	}

	public async getTotalSectionsByCourse(courseUuid: string): Promise<number> {
		return this.withLoading(async () => {
			return await firstValueFrom<number>(
				this.api.get<number>(`${this.BASE_PATH}/course/${courseUuid}/total-sections`)
			);
		});
	}

	public async findFirstSectionMatchingUuids$(
		params: Record<string, string>
	): Promise<Section | undefined> {
		return this.withLoading(async () => {
			const { clientUuid, courseUuid, sectionUuid, lessonUuid, activityUuid } = params;

			if (sectionUuid) {
				try {
					const section = await this.get$(sectionUuid);
					return section;
				} catch (error) {
					// Section not found, continue with other search methods
				}
			}

			const sections = await this.getSectionsByClient$(clientUuid, 0, 1);
			if (!sections.length) {
				return undefined;
			}

			if (courseUuid) {
				const courseSections = sections.filter(
					section => section.course_uuid === courseUuid
				);
				if (courseSections.length) {
					return courseSections[0];
				}
			}

			// If we have lessonUuid or activityUuid, we'll need to fetch the course structure
			// to find the corresponding course, then get its first section
			if (lessonUuid || activityUuid) {
				// Note: You'll need to inject CourseService if you want to implement this part
				return sections[0];
			}

			return sections[0];
		});
	}

	public async get$(uuid: string, includeUsers: boolean = false): Promise<Section> {
		return this.withLoading(async () => {
			const params = { includeUsers: includeUsers.toString() };
			return firstValueFrom(
				this.api.get<Section>(`${this.BASE_PATH}/get-by-uuid/${uuid}`, { params })
			);
		});
	}

	public async create$(sectionData: SectionCreatePayload): Promise<Section> {
		return this.withLoading(async () => {
			const newSection = await firstValueFrom(
				this.api.post<Section>(this.BASE_PATH, sectionData)
			);
			return newSection;
		});
	}

	public async update$(uuid: string, sectionData: SectionUpdatePayload): Promise<Section> {
		return this.withLoading(async () => {
			const updatedSection = await firstValueFrom(
				this.api.put<Section>(`${this.BASE_PATH}/${uuid}`, sectionData)
			);
			return updatedSection;
		});
	}

	public async delete$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete<void>(`${this.BASE_PATH}/${uuid}`));
		});
	}

	public async selfRosterStudentToSection$(sectionCode: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(
				this.api.post<void>(
					`${this.BASE_PATH}/self-roster/${sectionCode.toUpperCase().trim()}`,
					{}
				)
			);
		});
	}

	public async addUserToSection$(sectionUuid: string, cognitoUserUuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(
				this.api.post<void>(
					`${this.BASE_PATH}/${sectionUuid}/link-user/${cognitoUserUuid}`,
					{}
				)
			);
		});
	}

	public async removeUserFromSection$(
		sectionUuid: string,
		cognitoUserUuid: string
	): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(
				this.api.delete<void>(
					`${this.BASE_PATH}/${sectionUuid}/unlink-user/${cognitoUserUuid}`
				)
			);
		});
	}

	public async removeUserFromAllSections$(cognitoUserUuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(
				this.api.delete<void>(`${this.BASE_PATH}/unlink-user-from-all/${cognitoUserUuid}`)
			);
		});
	}

	public async getSectionUsers$(sectionUuid: string): Promise<User[]> {
		return this.withLoading(async () => {
			return firstValueFrom(this.api.get<User[]>(`${this.BASE_PATH}/users/${sectionUuid}`));
		});
	}
}
